﻿let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  Dimensions,
  ActivityIndicator,
  StatusBar,
  Linking,
  Alert,
  Share,
  Modal,
  Animated,
  RefreshControl,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  MaterialIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Import the unified activity tracker
import activityTracker from '../../services/UnifiedActivityTracker';
import { UNIFIED_JOB_DATA } from '../../services/UnifiedJobData';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

// JOBENG Logo component
const JobengLogo = () => (
  <View style={styles.logoContainer}>
    <Text style={styles.logoText}>JOBENG</Text>
  </View>
);

export default function ActivityDetailScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_activitydetailscreen`;
    apiFetch('/api/data/applications?sort=created_at&order=desc&limit=10').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // Route parameters
  const { activityId, activity: routeActivity, source } = route.params || {};

  // State management
  const [activity, setActivity] = useState(routeActivity || null);
  const [loading, setLoading] = useState(!routeActivity);
  const [refreshing, setRefreshing] = useState(false);
  const [relatedActivities, setRelatedActivities] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    timeline: true,
    details: true,
    metadata: false,
    related: false,
  });

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Initialize component
  useEffect(() => {
    initializeData();
    startAnimations();
  }, [activityId]);

  // Focus effect to refresh data when screen is focused
  useFocusEffect(
    useCallback(() => {
      if (activityId && !routeActivity) {
        loadActivityData();
      }
    }, [activityId])
  );

  // Initialize data
  const initializeData = async () => {
    try {
      console.log('🔄 Initializing ActivityDetailScreen...');
      
      if (!activity && activityId) {
        await loadActivityData();
      }
      
      if (activity) {
        await loadRelatedActivities();
      }
      
      console.log('✅ ActivityDetailScreen initialized successfully');
    } catch (error) {
      console.error('❌ Error initializing ActivityDetailScreen:', error);
      Alert.alert('Error', 'Failed to load activity details. Please try again.');
    }
  };

  // Load activity data from tracker
  const loadActivityData = async () => {
    try {
      setLoading(true);
      console.log('📱 Loading activity data for ID:', activityId);

      // Initialize activity tracker if needed
      if (!activityTracker.initialized) {
        await activityTracker.initialize();
      }

      // Get activity by ID
      const activityData = activityTracker.getActivityById(activityId);
      
      if (activityData) {
        setActivity(activityData);
        console.log('✅ Activity data loaded:', activityData.title);
      } else {
        console.warn('⚠️ Activity not found:', activityId);
        Alert.alert('Activity Not Found', 'The requested activity could not be found.');
        navigation.goBack();
      }
    } catch (error) {
      console.error('❌ Error loading activity data:', error);
      Alert.alert('Error', 'Failed to load activity details.');
    } finally {
      setLoading(false);
    }
  };

  // Load related activities
  const loadRelatedActivities = async () => {
    try {
      if (!activity) return;

      console.log('🔄 Loading related activities...');
      
      // Get related activities based on job ID, company, or category
      const allActivities = activityTracker.getActivities();
      const related = allActivities.filter(item => 
        item.id !== activity.id && (
          item.jobId === activity.jobId ||
          item.company === activity.company ||
          item.category === activity.category
        )
      ).slice(0, 5); // Limit to 5 related activities

      setRelatedActivities(related);
      console.log(`✅ Loaded ${related.length} related activities`);
    } catch (error) {
      console.error('❌ Error loading related activities:', error);
    }
  };

  // Start entrance animations
  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadActivityData();
    await loadRelatedActivities();
    setRefreshing(false);
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Navigate to related screen
  const navigateToRelatedScreen = () => {
    try {
      console.log('🧭 Navigating to related screen for:', activity.type);

      switch (activity.type) {
        case 'application_submitted':
        case 'application_rejected':
          if (activity.jobId) {
            navigation.navigate('JobApplications', {
              highlightApplication: activity.applicationId,
              source: 'ActivityDetailScreen'
            });
          }
          break;

        case 'interview_scheduled':
        case 'interview_completed':
          navigation.navigate('InterviewHistoryScreen', {
            highlightInterview: activity.jobId,
            source: 'ActivityDetailScreen'
          });
          break;

        case 'offer_received':
          if (activity.offerId) {
            navigation.navigate('OfferDetails', {
              offerId: activity.offerId,
              offer: activity.metadata,
              source: 'ActivityDetailScreen'
            });
          }
          break;

        case 'portfolio_updated':
          navigation.navigate('Portfolio', {
            source: 'ActivityDetailScreen'
          });
          break;

        case 'skill_assessment':
        case 'course_completed':
          navigation.navigate('TestsInterviews', {
            source: 'ActivityDetailScreen'
          });
          break;

        case 'webinar_registered':
          navigation.navigate('WebinarRegistrationScreen', {
            source: 'ActivityDetailScreen'
          });
          break;

        default:
          Alert.alert('Navigation', 'No specific screen available for this activity type.');
          break;
      }
    } catch (error) {
      console.error('❌ Navigation error:', error);
      Alert.alert('Navigation Error', 'Unable to navigate to related screen.');
    }
  };

  // Navigate to related activity
  const navigateToRelatedActivity = (relatedActivity) => {
    navigation.push('ActivityDetailScreen', {
      activityId: relatedActivity.id,
      activity: relatedActivity,
      source: 'ActivityDetailScreen'
    });
  };

  // Handle share activity
  const handleShare = async () => {
    try {
      const shareContent = {
        title: `JobEng Activity: ${activity.title}`,
        message: `${activity.title}\n${activity.subtitle}\n\nCompany: ${activity.company || 'N/A'}\nDate: ${activity.date}\nTime: ${activity.time}\n\nDescription: ${activity.description}\n\nShared from JobEng App`,
        url: 'https://jobeng.app', // Replace with your app's URL
      };

      await Share.share(shareContent);
      console.log('✅ Activity shared successfully');
    } catch (error) {
      console.error('❌ Error sharing activity:', error);
      Alert.alert('Share Error', 'Unable to share activity. Please try again.');
    }
  };

  // Handle contact action
  const handleContact = async (contactInfo) => {
    try {
      if (contactInfo.email) {
        const subject = `Regarding: ${activity.title}`;
        const body = `Hello,\n\nI would like to discuss the ${activity.title} activity.\n\nBest regards`;
        const url = `mailto:${contactInfo.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        
        try {
          await Linking.openURL(url);
        } catch (e) {
          Alert.alert('Email Not Available', 'Unable to open email client.');
        }
      } else if (contactInfo.phone) {
        const url = `tel:${contactInfo.phone}`;
        try {
          await Linking.openURL(url);
        } catch (e) {
          Alert.alert('Phone Not Available', 'Unable to make phone call.');
        }
      }
    } catch (error) {
      console.error('❌ Error handling contact:', error);
      Alert.alert('Contact Error', 'Unable to initiate contact. Please try again.');
    }
  };

  // Get activity icon
  const getActivityIcon = (activity) => {
    const iconMap = {
      'application_submitted': 'paper-plane',
      'application_rejected': 'close-circle',
      'interview_scheduled': 'calendar',
      'interview_completed': 'checkmark-circle',
      'offer_received': 'gift',
      'portfolio_updated': 'briefcase',
      'skill_assessment': 'school',
      'course_completed': 'book',
      'webinar_registered': 'videocam',
    };

    return iconMap[activity.type] || activity.icon || 'information-circle';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      high: '#EF4444',
      medium: '#F59E0B',
      low: '#10B981'
    };
    return colors[priority] || '#6B7280';
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      completed: '#10B981',
      pending: '#F59E0B',
      scheduled: '#3B82F6',
      cancelled: '#EF4444'
    };
    return colors[status] || '#6B7280';
  };

  // Generate timeline based on activity type
  const generateTimeline = (activity) => {
    const timeline = [];
    const baseDate = new Date(activity.timestamp);

    switch (activity.type) {
      case 'application_submitted':
        timeline.push(
          {
            id: 'submitted',
            title: 'Application Submitted',
            date: activity.date,
            time: activity.time,
            description: 'Your application has been successfully submitted.',
            isCompleted: true,
            icon: 'paper-plane',
            color: '#10B981'
          },
          {
            id: 'review',
            title: 'Under Review',
            date: 'Pending',
            time: '',
            description: 'Your application is being reviewed by the hiring team.',
            isCompleted: false,
            icon: 'eye',
            color: '#F59E0B',
            estimatedDate: new Date(baseDate.getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString()
          },
          {
            id: 'screening',
            title: 'Initial Screening',
            date: 'Pending',
            time: '',
            description: 'If selected, you will be contacted for screening.',
            isCompleted: false,
            icon: 'phone',
            color: '#6B7280',
            estimatedDate: new Date(baseDate.getTime() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString()
          }
        );
        break;

      case 'interview_scheduled':
        timeline.push(
          {
            id: 'scheduled',
            title: 'Interview Scheduled',
            date: activity.date,
            time: activity.time,
            description: 'Interview has been scheduled successfully.',
            isCompleted: true,
            icon: 'calendar',
            color: '#10B981'
          },
          {
            id: 'preparation',
            title: 'Interview Preparation',
            date: 'In Progress',
            time: '',
            description: 'Prepare for your upcoming interview.',
            isCompleted: false,
            icon: 'book',
            color: '#3B82F6'
          },
          {
            id: 'interview',
            title: 'Interview Day',
            date: activity.metadata?.scheduledDate ? new Date(activity.metadata.scheduledDate).toLocaleDateString() : 'TBD',
            time: activity.metadata?.scheduledTime || 'TBD',
            description: 'Attend your scheduled interview.',
            isCompleted: false,
            icon: 'people',
            color: '#F59E0B'
          }
        );
        break;

      case 'offer_received':
        timeline.push(
          {
            id: 'received',
            title: 'Offer Received',
            date: activity.date,
            time: activity.time,
            description: 'Job offer has been received.',
            isCompleted: true,
            icon: 'gift',
            color: '#10B981'
          },
          {
            id: 'review',
            title: 'Review Offer',
            date: 'In Progress',
            time: '',
            description: 'Review the offer details and terms.',
            isCompleted: false,
            icon: 'document-text',
            color: '#3B82F6'
          },
          {
            id: 'decision',
            title: 'Make Decision',
            date: activity.metadata?.deadline || 'TBD',
            time: '',
            description: 'Accept, reject, or negotiate the offer.',
            isCompleted: false,
            icon: 'checkmark-circle',
            color: '#F59E0B'
          }
        );
        break;

      default:
        timeline.push({
          id: 'completed',
          title: activity.title,
          date: activity.date,
          time: activity.time,
          description: activity.description,
          isCompleted: true,
          icon: getActivityIcon(activity),
          color: activity.color || '#10B981'
        });
        break;
    }

    return timeline;
  };

  // Render timeline item
  const renderTimelineItem = (item, index, timeline) => (
    <View key={item.id} style={styles.timelineItem}>
      <View style={styles.timelineIconContainer}>
        <View style={[styles.timelineIcon, { backgroundColor: item.color }]}>
          <Ionicons name={item.icon} size={16} color="#FFFFFF" />
        </View>
        {index < timeline.length - 1 && (
          <View style={[styles.timelineLine, { backgroundColor: item.isCompleted ? item.color : '#E5E7EB' }]} />
        )}
      </View>
      
      <View style={styles.timelineContent}>
        <View style={styles.timelineHeader}>
          <Text style={[styles.timelineTitle, { opacity: item.isCompleted ? 1 : 0.7 }]}>
            {item.title}
          </Text>
          <View style={styles.timelineDate}>
            <Text style={styles.timelineDateText}>{item.date}</Text>
            {item.time && <Text style={styles.timelineTimeText}>{item.time}</Text>}
          </View>
        </View>
        
        <Text style={[styles.timelineDescription, { opacity: item.isCompleted ? 1 : 0.7 }]}>
          {item.description}
        </Text>
        
        {item.estimatedDate && (
          <Text style={styles.timelineEstimate}>
            Estimated: {item.estimatedDate}
          </Text>
        )}
      </View>
    </View>
  );

  // Render metadata section
  const renderMetadata = () => {
    if (!activity.metadata || Object.keys(activity.metadata).length === 0) {
      return null;
    }

    return (
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => toggleSection('metadata')}
        >
          <Text style={styles.sectionTitle}>Additional Details</Text>
          <Ionicons
            name={expandedSections.metadata ? 'chevron-up' : 'chevron-down'}
            size={20}
            color="#6B7280"
          />
        </TouchableOpacity>
        
        {expandedSections.metadata && (
          <View style={styles.sectionContent}>
            {Object.entries(activity.metadata).map(([key, value]) => {
              if (typeof value === 'object' && value !== null) {
                return null; // Skip complex objects for now
              }
              
              return (
                <View key={key} style={styles.metadataItem}>
                  <Text style={styles.metadataKey}>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                  </Text>
                  <Text style={styles.metadataValue}>{String(value)}</Text>
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  };

  // Render related activities
  const renderRelatedActivities = () => {
    if (relatedActivities.length === 0) {
      return null;
    }

    return (
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.sectionHeader}
          onPress={() => toggleSection('related')}
        >
          <Text style={styles.sectionTitle}>Related Activities</Text>
          <Ionicons
            name={expandedSections.related ? 'chevron-up' : 'chevron-down'}
            size={20}
            color="#6B7280"
          />
        </TouchableOpacity>
        
        {expandedSections.related && (
          <View style={styles.sectionContent}>
            {relatedActivities.map((relatedActivity, index) => (
              <TouchableOpacity
                key={relatedActivity.id}
                style={styles.relatedActivityItem}
                onPress={() => navigateToRelatedActivity(relatedActivity)}
              >
                <View style={[styles.relatedActivityIcon, { backgroundColor: relatedActivity.color || '#6B7280' }]}>
                  <Ionicons name={getActivityIcon(relatedActivity)} size={16} color="#FFFFFF" />
                </View>
                
                <View style={styles.relatedActivityContent}>
                  <Text style={styles.relatedActivityTitle} numberOfLines={1}>
                    {relatedActivity.title}
                  </Text>
                  <Text style={styles.relatedActivitySubtitle} numberOfLines={1}>
                    {relatedActivity.subtitle}
                  </Text>
                  <Text style={styles.relatedActivityDate}>
                    {relatedActivity.date}
                  </Text>
                </View>
                
                <Ionicons name="chevron-forward" size={16} color="#6B7280" />
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>
    );
  };

  // Render action buttons
  const renderActionButtons = () => (
    <View style={styles.actionButtons}>
      <TouchableOpacity
        style={[styles.actionButton, styles.primaryButton]}
        onPress={navigateToRelatedScreen}
      >
        <Ionicons name="open" size={20} color="#FFFFFF" />
        <Text style={styles.actionButtonText}>View Details</Text>
      </TouchableOpacity>
      
      <TouchableOpacity
        style={[styles.actionButton, styles.secondaryButton]}
        onPress={handleShare}
      >
        <Ionicons name="share" size={20} color="#0e4e96" />
        <Text style={[styles.actionButtonText, { color: '#0e4e96' }]}>Share</Text>
      </TouchableOpacity>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading activity details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!activity) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={64} color="#EF4444" />
          <Text style={styles.errorTitle}>Activity Not Found</Text>
          <Text style={styles.errorText}>
            The requested activity could not be found or has been removed.
          </Text>
          <TouchableOpacity
            style={styles.errorButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const timeline = generateTimeline(activity);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <JobengLogo />
        
        <View style={styles.headerActions}>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowActionsModal(true)}
          >
            <Ionicons name="ellipsis-vertical" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={['#0e4e96']}
            tintColor="#0e4e96"
          />
        }
      >
        <Animated.View
          style={[
            styles.content,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {/* Activity Header */}
          <View style={styles.activityHeader}>
            <View style={styles.activityIconContainer}>
              <View style={[styles.activityIcon, { backgroundColor: activity.color || '#6B7280' }]}>
                <Ionicons name={getActivityIcon(activity)} size={32} color="#FFFFFF" />
              </View>
              <View style={[styles.priorityIndicator, { backgroundColor: getPriorityColor(activity.priority) }]} />
            </View>
            
            <View style={styles.activityInfo}>
              <Text style={styles.activityTitle}>{activity.title}</Text>
              <Text style={styles.activitySubtitle}>{activity.subtitle}</Text>
              
              {activity.company && (
                <View style={styles.companyInfo}>
                  {activity.metadata?.companyLogo && (
                    <Image source={{ uri: (activity.metadata.companyLogo && typeof activity.metadata.companyLogo=="string" && activity.metadata.companyLogo.startsWith("http")) ? activity.metadata.companyLogo : undefined }} style={styles.companyLogo} />
                  )}
                  <Text style={styles.companyName}>{activity.company}</Text>
                </View>
              )}
              
              <View style={styles.activityMeta}>
                <View style={styles.metaItem}>
                  <Ionicons name="calendar" size={16} color="#6B7280" />
                  <Text style={styles.metaText}>{activity.date}</Text>
                </View>
                <View style={styles.metaItem}>
                  <Ionicons name="time" size={16} color="#6B7280" />
                  <Text style={styles.metaText}>{activity.time}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(activity.status) }]}>
                  <Text style={styles.statusText}>{activity.status}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Description */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Description</Text>
            <Text style={styles.description}>{activity.description}</Text>
          </View>

          {/* Timeline */}
          <View style={styles.section}>
            <TouchableOpacity
              style={styles.sectionHeader}
              onPress={() => toggleSection('timeline')}
            >
              <Text style={styles.sectionTitle}>Timeline</Text>
              <Ionicons
                name={expandedSections.timeline ? 'chevron-up' : 'chevron-down'}
                size={20}
                color="#6B7280"
              />
            </TouchableOpacity>
            
            {expandedSections.timeline && (
              <View style={styles.timeline}>
                {timeline.map((item, index) => renderTimelineItem(item, index, timeline))}
              </View>
            )}
          </View>

          {/* Metadata */}
          {renderMetadata()}

          {/* Related Activities */}
          {renderRelatedActivities()}

          {/* Action Buttons */}
          {renderActionButtons()}
        </Animated.View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#111827',
    marginTop: 16,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
  },
  errorButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoContainer: {
    flex: 1,
    alignItems: 'center',
  },
  logoText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: 1,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
  },
  activityHeader: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  activityIconContainer: {
    position: 'relative',
    marginRight: 16,
  },
  activityIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  priorityIndicator: {
    position: 'absolute',
    top: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  activityInfo: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
    marginBottom: 4,
  },
  activitySubtitle: {
    fontSize: 16,
    color: '#6B7280',
    fontWeight: '500',
    marginBottom: 12,
  },
  companyInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  companyLogo: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 8,
  },
  companyName: {
    fontSize: 16,
    color: '#374151',
    fontWeight: '600',
  },
  activityMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  metaText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    marginLeft: 8,
  },
  statusText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  sectionContent: {
    padding: 20,
    paddingTop: 0,
  },
  description: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 24,
    padding: 20,
  },
  timeline: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 20,
  },
  timelineIconContainer: {
    alignItems: 'center',
    marginRight: 16,
  },
  timelineIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  timelineLine: {
    width: 2,
    flex: 1,
    marginTop: 8,
  },
  timelineContent: {
    flex: 1,
  },
  timelineHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  timelineTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    flex: 1,
    marginRight: 8,
  },
  timelineDate: {
    alignItems: 'flex-end',
  },
  timelineDateText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  timelineTimeText: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  timelineDescription: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
    marginBottom: 4,
  },
  timelineEstimate: {
    fontSize: 12,
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  metadataItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F9FAFB',
  },
  metadataKey: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
    flex: 1,
  },
  metadataValue: {
    fontSize: 14,
    color: '#111827',
    fontWeight: '500',
    textAlign: 'right',
    flex: 1,
  },
  relatedActivityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  relatedActivityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  relatedActivityContent: {
    flex: 1,
  },
  relatedActivityTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 2,
  },
  relatedActivitySubtitle: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  relatedActivityDate: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  actionButtons: {
    flexDirection: 'row',
    marginTop: 20,
    marginBottom: 40,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    marginHorizontal: 4,
  },
  primaryButton: {
    backgroundColor: '#0e4e96',
  },
  secondaryButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#0e4e96',
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
});
